import { throttle } from '../utils/PerformanceUtils.js';

/**
 * Sandbox Panel - Right sidebar for editing virtual tours
 * Uses tabs instead of accordions for better space management
 * Overlay mode (doesn't reduce viewer size)
 */
export class SandboxPanel {
    constructor() {
        this.panel = null;
        this.reopenButton = null;
        this.isOpen = true;
        this.width = 600; // Default width in pixels
        this.minWidth = 500;
        this.maxWidth = null; // Will be calculated as 75% of window width
        this.isResizing = false;
        this.activeTab = 'graph'; // Default active tab

        // Callbacks
        this.onSave = null;
        this.onImageSelected = null;
        this.onImageDeleted = null;
        this.onImageRenamed = null;
        this.onCustomImageAdded = null;
        this.onCustomImageDeleted = null;
        this.onLogoUploaded = null;
        this.onLogoPositionChanged = null;
        this.onLogoDeleted = null;
        this.onGraphTabActivated = null;

        // Event handlers (stored for cleanup)
        this.boundResizeHandler = null;
    }

    /**
     * Initialize and inject the panel into the DOM
     */
    initialize() {
        this.calculateMaxWidth();
        this.createPanelStructure();
        this.createReopenButton();
        this.attachEventListeners();

        // Create throttled resize handler (120ms)
        this.boundResizeHandler = throttle(() => {
            this.calculateMaxWidth();
            if (this.width > this.maxWidth) {
                this.width = this.maxWidth;
                this.panel.style.width = `${this.width}px`;
            }
        }, 120);

        // Recalculate max width on window resize with throttling
        window.addEventListener('resize', this.boundResizeHandler);

        console.log('✅ SandboxPanel initialized');
    }

    /**
     * Calculate max width (75% of window width)
     */
    calculateMaxWidth() {
        this.maxWidth = Math.floor(window.innerWidth * 0.75);
    }

    /**
     * Create the HTML structure for the panel
     */
    createPanelStructure() {
        const panel = document.createElement('div');
        panel.id = 'sandbox-panel';
        panel.className = 'fixed top-0 right-0 h-full bg-black/80 backdrop-blur-md border-l border-white/10 z-40 flex flex-col transition-transform duration-300';
        panel.style.width = `${this.width}px`;

        panel.innerHTML = `
            <!-- Panel Header -->
            <div class="flex items-center justify-between px-4 py-3 border-b border-white/10 flex-shrink-0">
                <h2 class="text-lg font-bold text-white">Mode Sandbox</h2>
                <div class="flex items-center space-x-2">
                    <button id="sandbox-save-btn" class="px-3 py-1.5 bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white text-xs font-semibold rounded-lg transition-all duration-300">
                        💾 Sauvegarder
                    </button>
                    <button id="sandbox-toggle-btn" class="text-slate-400 hover:text-white transition-colors p-1 hover:bg-white/10 rounded-lg">
                        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                        </svg>
                    </button>
                </div>
            </div>

            <!-- Tabs Navigation -->
            <div class="flex border-b border-white/10 flex-shrink-0">
                <button class="sandbox-tab flex-1 px-4 py-3 text-sm font-medium text-white bg-slate-800/50 border-b-2 border-blue-500 transition-all" data-tab="graph">
                    🗺️ Graph
                </button>
                <button class="sandbox-tab flex-1 px-4 py-3 text-sm font-medium text-slate-400 hover:text-white hover:bg-slate-800/30 border-b-2 border-transparent transition-all" data-tab="custom-images">
                    🖼️ Images
                </button>
                <button class="sandbox-tab flex-1 px-4 py-3 text-sm font-medium text-slate-400 hover:text-white hover:bg-slate-800/30 border-b-2 border-transparent transition-all" data-tab="logo">
                    🏷️ Logo
                </button>
                <button class="sandbox-tab flex-1 px-4 py-3 text-sm font-medium text-slate-400 hover:text-white hover:bg-slate-800/30 border-b-2 border-transparent transition-all" data-tab="tripod">
                    🎭 Trépied
                </button>
            </div>

            <!-- Tab Content Container -->
            <div class="flex-1 overflow-hidden">

                <!-- Graph Tab -->
                <div id="tab-graph" class="tab-content h-full flex flex-col p-4">
                    <!-- Help Text (above graph) -->
                    <div id="graph-help-text" class="text-xs text-slate-300 bg-slate-800/60 rounded-lg px-3 py-2 mb-2 min-h-[36px] flex items-center justify-center transition-all duration-200 border border-slate-700/50">
                        <span class="flex items-center gap-3">
                            <span class="flex items-center gap-1.5">
                                <svg class="w-3.5 h-3.5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122"/></svg>
                                <span>Cliquez pour voir</span>
                            </span>
                            <span class="text-slate-600">|</span>
                            <span class="flex items-center gap-1.5">
                                <svg class="w-3.5 h-3.5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"/></svg>
                                <span>Glissez pour déplacer</span>
                            </span>
                            <span class="text-slate-600">|</span>
                            <span class="flex items-center gap-1.5">
                                <svg class="w-3.5 h-3.5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"/></svg>
                                <span>Clic droit pour options</span>
                            </span>
                        </span>
                    </div>

                    <!-- Graph Canvas Container (takes all available space) -->
                    <div id="graph-canvas-container" class="flex-1 w-full bg-slate-950 rounded-lg border border-slate-700 relative overflow-hidden mb-3">
                        <canvas id="graph-canvas" class="w-full h-full block"></canvas>
                        <div class="absolute top-2 right-2 flex space-x-1">
                            <button id="graph-zoom-in" class="p-1.5 bg-black/50 hover:bg-black/70 text-white rounded transition-colors" title="Zoom in">
                                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
                                </svg>
                            </button>
                            <button id="graph-zoom-out" class="p-1.5 bg-black/50 hover:bg-black/70 text-white rounded transition-colors" title="Zoom out">
                                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 12H4"/>
                                </svg>
                            </button>
                            <button id="graph-reset-view" class="p-1.5 bg-black/50 hover:bg-black/70 text-white rounded transition-colors" title="Reset view">
                                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                                </svg>
                            </button>
                        </div>
                        <div class="absolute bottom-2 left-2 text-xs text-slate-400 bg-black/50 px-2 py-1 rounded">
                            <span id="graph-info">0 image(s), 0 connexion(s)</span>
                        </div>
                    </div>

                    <!-- Graph Controls (fixed at bottom) -->
                    <div class="space-y-2 flex-shrink-0">
                        <button id="graph-add-image" class="w-full px-4 py-2.5 bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white text-sm font-medium rounded-lg transition-all duration-300">
                            + Ajouter une image 360°
                        </button>

                        <div class="relative group">
                            <button id="graph-create-connection" class="w-full px-4 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white text-sm font-medium rounded-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:from-gray-600 disabled:to-gray-700">
                                🔗 Créer une connexion
                            </button>
                            <!-- Tooltip for disabled state -->
                            <div id="graph-connection-tooltip" class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg whitespace-nowrap opacity-0 pointer-events-none transition-opacity duration-200 hidden group-hover:opacity-100">
                                Au moins 2 images sont nécessaires
                                <div class="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Custom Images Tab -->
                <div id="tab-custom-images" class="tab-content p-4 hidden">
                    <div class="space-y-4">
                        <div class="text-sm text-slate-300">
                            <p class="mb-2">Images personnalisées pour la scène actuelle</p>
                        </div>

                        <div id="custom-images-list" class="space-y-2 max-h-96 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-600 scrollbar-track-slate-800">
                            <div class="text-xs text-slate-500 text-center py-8 bg-slate-900/30 rounded-lg border border-dashed border-slate-700">
                                Aucune image personnalisée dans cette scène
                            </div>
                        </div>

                        <button id="custom-image-add" class="w-full px-4 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white text-sm font-medium rounded-lg transition-all duration-300">
                            + Ajouter une image personnalisée
                        </button>
                    </div>
                </div>

                <!-- Logo Tab -->
                <div id="tab-logo" class="tab-content p-4 hidden">
                    <div class="space-y-4">
                        <!-- Description -->
                        <div class="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                            <div class="flex items-start gap-3">
                                <svg class="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                </svg>
                                <div class="text-sm text-slate-300 leading-relaxed">
                                    <p class="font-medium text-white mb-1">Personnalisation du branding</p>
                                    <p>Ajoutez votre logo ou celui de votre client pour personnaliser la visite virtuelle. Le logo sera visible en permanence sur toutes les scènes.</p>
                                </div>
                            </div>
                        </div>

                        <!-- Logo Preview -->
                        <div id="logo-preview-container" class="hidden">
                            <div class="w-full h-32 bg-slate-800 rounded-lg border border-slate-600 flex items-center justify-center p-2">
                                <img id="logo-preview-img" class="max-w-full max-h-full object-contain" alt="Logo preview">
                            </div>
                        </div>

                        <!-- Logo Upload -->
                        <button id="logo-upload-btn" class="w-full px-4 py-2.5 bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white text-sm font-medium rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl">
                            <span class="flex items-center justify-center gap-2">
                                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                                </svg>
                                <span id="logo-upload-btn-text">Charger un logo</span>
                            </span>
                        </button>

                        <!-- Delete Logo -->
                        <button id="logo-delete-btn" class="hidden w-full px-4 py-2.5 bg-red-600/80 hover:bg-red-600 text-white text-sm font-medium rounded-lg transition-all duration-300">
                            <span class="flex items-center justify-center gap-2">
                                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                                </svg>
                                <span>Supprimer le logo</span>
                            </span>
                        </button>

                        <!-- Position Selection -->
                        <div id="logo-position-selector" class="hidden space-y-2 bg-slate-900/50 rounded-lg p-3">
                            <label class="text-xs text-slate-300 font-medium flex items-center gap-2">
                                <svg class="w-3.5 h-3.5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                                </svg>
                                Position du logo
                            </label>
                            <div class="grid grid-cols-3 gap-2">
                                <button class="logo-position-btn group p-2 bg-slate-700 hover:bg-slate-600 rounded text-xs text-white transition-all hover:scale-105" data-position="top-left">
                                    <div class="w-full aspect-square border border-slate-500 group-hover:border-blue-400 rounded mb-1 relative transition-colors">
                                        <div class="absolute top-0.5 left-0.5 w-1.5 h-1.5 bg-blue-400 rounded-full"></div>
                                    </div>
                                    <div class="text-[10px] leading-tight">Haut gauche</div>
                                </button>
                                <button class="logo-position-btn group p-2 bg-slate-700 hover:bg-slate-600 rounded text-xs text-white transition-all hover:scale-105" data-position="top-center">
                                    <div class="w-full aspect-square border border-slate-500 group-hover:border-blue-400 rounded mb-1 relative transition-colors">
                                        <div class="absolute top-0.5 left-1/2 -translate-x-1/2 w-1.5 h-1.5 bg-blue-400 rounded-full"></div>
                                    </div>
                                    <div class="text-[10px] leading-tight">Haut centre</div>
                                </button>
                                <button class="logo-position-btn group p-2 bg-slate-700 hover:bg-slate-600 rounded text-xs text-white transition-all hover:scale-105" data-position="top-right">
                                    <div class="w-full aspect-square border border-slate-500 group-hover:border-blue-400 rounded mb-1 relative transition-colors">
                                        <div class="absolute top-0.5 right-0.5 w-1.5 h-1.5 bg-blue-400 rounded-full"></div>
                                    </div>
                                    <div class="text-[10px] leading-tight">Haut droit</div>
                                </button>
                                <button class="logo-position-btn group p-2 bg-slate-700 hover:bg-slate-600 rounded text-xs text-white transition-all hover:scale-105" data-position="bottom-left">
                                    <div class="w-full aspect-square border border-slate-500 group-hover:border-blue-400 rounded mb-1 relative transition-colors">
                                        <div class="absolute bottom-0.5 left-0.5 w-1.5 h-1.5 bg-blue-400 rounded-full"></div>
                                    </div>
                                    <div class="text-[10px] leading-tight">Bas gauche</div>
                                </button>
                                <button class="logo-position-btn group p-2 bg-slate-700 hover:bg-slate-600 rounded text-xs text-white transition-all hover:scale-105" data-position="center">
                                    <div class="w-full aspect-square border border-slate-500 group-hover:border-blue-400 rounded mb-1 relative transition-colors">
                                        <div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1.5 h-1.5 bg-blue-400 rounded-full"></div>
                                    </div>
                                    <div class="text-[10px] leading-tight">Centre</div>
                                </button>
                                <button class="logo-position-btn group p-2 bg-slate-700 hover:bg-slate-600 rounded text-xs text-white transition-all hover:scale-105" data-position="bottom-right">
                                    <div class="w-full aspect-square border border-slate-500 group-hover:border-blue-400 rounded mb-1 relative transition-colors">
                                        <div class="absolute bottom-0.5 right-0.5 w-1.5 h-1.5 bg-blue-400 rounded-full"></div>
                                    </div>
                                    <div class="text-[10px] leading-tight">Bas droit</div>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Tripod Tab -->
                <div id="tab-tripod" class="tab-content p-4 hidden">
                    <div class="space-y-4">
                        <!-- Description -->
                        <div class="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                            <div class="flex items-start gap-3">
                                <svg class="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                </svg>
                                <div class="text-sm text-slate-300 leading-relaxed">
                                    <p class="font-medium text-white mb-1">Masque de trépied</p>
                                    <p>Ajoutez une image pour masquer le trépied visible au bas de vos photos 360°. Le masque sera appliqué à toutes les scènes.</p>
                                </div>
                            </div>
                        </div>

                        <!-- Tripod Preview -->
                        <div id="tripod-preview-container" class="hidden">
                            <div class="w-full h-32 bg-slate-800 rounded-lg border border-slate-600 flex items-center justify-center p-2">
                                <img id="tripod-preview-img" class="max-w-full max-h-full object-contain" alt="Masque trépied preview">
                            </div>
                        </div>

                        <!-- Tripod Upload -->
                        <button id="tripod-load-btn" class="w-full px-4 py-2.5 bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white text-sm font-medium rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl">
                            <span class="flex items-center justify-center gap-2">
                                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                                </svg>
                                <span id="tripod-load-btn-text">Charger masque trépied</span>
                            </span>
                        </button>

                        <!-- Delete Tripod -->
                        <button id="tripod-delete-btn" class="hidden w-full px-4 py-2.5 bg-red-600/80 hover:bg-red-600 text-white text-sm font-medium rounded-lg transition-all duration-300">
                            <span class="flex items-center justify-center gap-2">
                                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                                </svg>
                                <span>Supprimer le masque</span>
                            </span>
                        </button>

                        <!-- Size Control -->
                        <div id="tripod-size-control" class="hidden space-y-3 bg-slate-900/50 rounded-lg p-4">
                            <label class="text-sm text-slate-300 font-medium flex items-center gap-2">
                                <svg class="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"/>
                                </svg>
                                Taille du masque
                            </label>
                            <input type="range" id="tripod-size-slider" min="0.1" max="3.0" step="0.1" value="1.0"
                                   class="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer">
                            <div class="text-sm text-slate-400 text-center font-mono" id="tripod-size-display">1.0x</div>
                        </div>
                    </div>
                </div>

            </div>

            <!-- Resize Handle -->
            <div id="sandbox-resize-handle" class="absolute top-0 left-0 w-1 h-full cursor-ew-resize hover:bg-blue-500/50 transition-colors"></div>
        `;

        document.body.appendChild(panel);
        this.panel = panel;
    }

    /**
     * Create the reopen button (shown when panel is closed)
     */
    createReopenButton() {
        const button = document.createElement('button');
        button.id = 'sandbox-reopen-btn';
        button.className = 'fixed top-6 right-6 z-30 bg-black/90 backdrop-blur-md rounded-xl px-4 py-3 border border-white/10 hover:bg-black/70 transition-all duration-300 shadow-lg hidden';
        button.innerHTML = `
            <div class="flex items-center space-x-2">
                <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z"/>
                </svg>
                <span class="text-white text-sm font-medium">Ouvrir Sandbox</span>
            </div>
        `;

        document.body.appendChild(button);
        this.reopenButton = button;

        // Add click event to reopen
        button.addEventListener('click', () => this.open());
    }

    /**
     * Attach event listeners
     */
    attachEventListeners() {
        // Toggle panel
        const toggleBtn = document.getElementById('sandbox-toggle-btn');
        toggleBtn?.addEventListener('click', () => this.toggle());

        // Save button
        const saveBtn = document.getElementById('sandbox-save-btn');
        saveBtn?.addEventListener('click', () => {
            if (this.onSave) this.onSave();
        });

        // Tab switching
        this.setupTabs();

        // Resize functionality
        this.setupResize();

        console.log('✅ SandboxPanel event listeners attached');
    }

    /**
     * Setup tab switching functionality
     */
    setupTabs() {
        const tabs = this.panel.querySelectorAll('.sandbox-tab');
        tabs.forEach(tab => {
            tab.addEventListener('click', () => {
                const tabName = tab.getAttribute('data-tab');
                this.switchTab(tabName);
            });
        });
    }

    /**
     * Switch to a specific tab
     */
    switchTab(tabName) {
        this.activeTab = tabName;

        // Update tab buttons
        const tabs = this.panel.querySelectorAll('.sandbox-tab');
        tabs.forEach(tab => {
            const isActive = tab.getAttribute('data-tab') === tabName;
            if (isActive) {
                tab.classList.add('bg-slate-800/50', 'text-white', 'border-blue-500');
                tab.classList.remove('text-slate-400', 'border-transparent');
            } else {
                tab.classList.remove('bg-slate-800/50', 'text-white', 'border-blue-500');
                tab.classList.add('text-slate-400', 'border-transparent');
            }
        });

        // Update tab content
        const contents = this.panel.querySelectorAll('.tab-content');
        contents.forEach(content => {
            const contentId = content.id;
            const isActive = contentId === `tab-${tabName}`;
            if (isActive) {
                content.classList.remove('hidden');
            } else {
                content.classList.add('hidden');
            }
        });

        // Trigger graph resize when switching to graph tab
        if (tabName === 'graph' && this.onGraphTabActivated) {
            // Delay to ensure content is visible before resize
            setTimeout(() => this.onGraphTabActivated(), 50);
        }
    }

    /**
     * Setup resize functionality
     */
    setupResize() {
        const handle = document.getElementById('sandbox-resize-handle');

        handle.addEventListener('mousedown', (e) => {
            this.isResizing = true;
            document.body.style.cursor = 'ew-resize';
            document.body.style.userSelect = 'none';
            e.preventDefault();
        });

        document.addEventListener('mousemove', (e) => {
            if (!this.isResizing) return;

            const newWidth = window.innerWidth - e.clientX;
            if (newWidth >= this.minWidth && newWidth <= this.maxWidth) {
                this.width = newWidth;
                this.panel.style.width = `${this.width}px`;
            }
        });

        document.addEventListener('mouseup', () => {
            if (this.isResizing) {
                this.isResizing = false;
                document.body.style.cursor = '';
                document.body.style.userSelect = '';

                // Trigger graph resize after manual resize ends
                if (this.onGraphTabActivated) {
                    this.onGraphTabActivated();
                }
            }
        });
    }

    /**
     * Toggle panel open/close
     */
    toggle() {
        this.isOpen = !this.isOpen;

        if (this.isOpen) {
            this.panel.style.transform = 'translateX(0)';
            this.reopenButton.classList.add('hidden');
        } else {
            this.panel.style.transform = `translateX(${this.width}px)`;
            this.reopenButton.classList.remove('hidden');
        }

        // Update toggle button icon
        const toggleBtn = document.getElementById('sandbox-toggle-btn');
        const svg = toggleBtn.querySelector('svg path');
        if (this.isOpen) {
            svg.setAttribute('d', 'M9 5l7 7-7 7'); // Arrow right (close)
        } else {
            svg.setAttribute('d', 'M15 19l-7-7 7-7'); // Arrow left (open)
        }
    }

    /**
     * Open the panel if closed
     */
    open() {
        if (!this.isOpen) {
            this.toggle();
        }
    }

    /**
     * Close the panel if open
     */
    close() {
        if (this.isOpen) {
            this.toggle();
        }
    }

    /**
     * Update graph info display
     */
    updateGraphInfo(imageCount, connectionCount) {
        const graphInfo = document.getElementById('graph-info');
        if (graphInfo) {
            graphInfo.textContent = `${imageCount} image(s), ${connectionCount} connexion(s)`;
        }
    }

    /**
     * Show/hide the panel based on mode
     */
    show() {
        this.panel.classList.remove('hidden');
    }

    hide() {
        this.panel.classList.add('hidden');
        this.reopenButton.classList.add('hidden');
    }

    /**
     * Minimize the panel (reduce width for placement mode)
     */
    minimize() {
        if (!this.isOpen) return;

        this.panel.style.width = '400px'; // Reduced width
        this.panel.style.transition = 'width 0.3s ease';

        // Trigger graph resize after transition
        if (this.onGraphTabActivated) {
            setTimeout(() => this.onGraphTabActivated(), 350);
        }
    }

    /**
     * Restore the panel to normal width
     */
    restore() {
        if (!this.isOpen) return;

        this.panel.style.width = `${this.width}px`; // Restore original width
        this.panel.style.transition = 'width 0.3s ease';

        // Trigger graph resize after transition
        if (this.onGraphTabActivated) {
            setTimeout(() => this.onGraphTabActivated(), 350);
        }
    }

    /**
     * Dispose of the SandboxPanel and clean up resources
     */
    dispose() {
        console.log('[SandboxPanel] Disposing...');

        // Remove window resize listener
        if (this.boundResizeHandler) {
            window.removeEventListener('resize', this.boundResizeHandler);
            this.boundResizeHandler = null;
        }

        // Remove panel from DOM
        if (this.panel && this.panel.parentNode) {
            this.panel.parentNode.removeChild(this.panel);
            this.panel = null;
        }

        // Remove reopen button from DOM
        if (this.reopenButton && this.reopenButton.parentNode) {
            this.reopenButton.parentNode.removeChild(this.reopenButton);
            this.reopenButton = null;
        }

        // Clear callbacks
        this.onSave = null;
        this.onImageSelected = null;
        this.onImageDeleted = null;
        this.onImageRenamed = null;
        this.onCustomImageAdded = null;
        this.onCustomImageDeleted = null;
        this.onLogoUploaded = null;
        this.onLogoPositionChanged = null;
        this.onLogoDeleted = null;
        this.onGraphTabActivated = null;

        console.log('[SandboxPanel] ✅ Disposed');
    }
}
